/* =========================================================
   Organization detail — three views.
     A "Clásica"   horizontal summary strip + section grid
     B "Secciones" sticky section rail + stacked sections
     C "Pestañas"  summary header + horizontal section tabs (default)

   The legal-contact block is read-only for every partner role;
   changing it goes through "Solicitar cambio" with an official
   appointment document attached.
   ========================================================= */
import React, { useEffect, useReducer, useState, type ReactNode } from 'react';
import type { Organization, Role, Site } from '@/types';
import { ic } from '@/components/Icons';
import {
  EmptyState, FieldLegend, FieldRow, PageHeader, ProgressBar, VariantSwitch,
  estiloPestana, puntoPestana,
} from '@/components/ui';
import { ComplianceCard } from '@/screens/compliance/ComplianceCard';
import { COUNTRIES, ds, userOrgIds } from '@/data';
import { useI18n } from '@/i18n';
import { orgSchema, validateField } from '@/lib/fields';
import { progresoOrg } from '@/lib/progresoPortal';
import { useGo } from '@/hooks/useRoute';
import { useAppStore } from '@/store/useAppStore';
import { usePuedeEditar, type PermisoEdicion } from '@/hooks/usePuedeEditar';
import { LegalChangeModal, OrgSites, SectionBlock } from './parts';

const SECTIONS = [
  // primera a propósito: es lo único que el socio tiene que contestar de
  // cero, y enterrarla debajo de seis bloques que ya vienen llenos sería
  // esconder justo el trabajo que se le pide
  { k: 'perfil', tone: 'var(--st-req)' },
  { k: 'general', tone: 'var(--brand-primary)' },
  { k: 'registered_address', tone: 'var(--st-edit)' },
  { k: 'contract_address', tone: 'var(--st-read)' },
  { k: 'billing_address', tone: 'var(--st-edit)' },
  { k: 'billing_contact', tone: 'var(--st-edit)' },
  { k: 'legal_contact', tone: 'var(--st-read)' },
];

/** El borrador de una ficha en edición.
 *
 *  Los valores son texto salvo los campos «multi» —industria, portafolio,
 *  entorno de formación— que son listas de códigos. */
type Draft = Record<string, string | string[] | undefined>;

interface DetailProps {
  role: Role;
  org: Organization;
  sites: Site[];
  instrCount: number;
  editing: boolean;
  setEditing: (v: boolean) => void;
  draft: Draft;
  setField: (k: string, v: string | string[]) => void;
  save: () => void;
  openLegal: () => void;
  legalPending: boolean;
  /** Si el servidor va a rechazar el guardado, no se ofrece el botón. */
  permiso: PermisoEdicion;
}

/** Legal-contact section actions: pending chip + request button. */
function LegalActions({ permiso, openLegal, legalPending }: Pick<DetailProps, 'permiso' | 'openLegal' | 'legalPending'>) {
  const { t } = useI18n();
  return (
    <>
      {legalPending ? <span className="chip warn">{t('legal.status_pending')}</span> : null}
      {/* Cambiar el contacto legal es un cambio de la organización: el
          servidor lo comprueba con writableOrgIds, que a un admin de site no
          le concede nada. */}
      <button
        className="btn sm"
        onClick={openLegal}
        disabled={legalPending || !permiso.puede}
        data-tip={!permiso.puede ? permiso.motivo : undefined}
      >
        {ic.edit} {t('legal.request_change')}
      </button>
    </>
  );
}

/* ---------- A: classic ---------- */

function Stat({ label, value, mono = true }: { label: ReactNode; value: ReactNode; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>{label}</div>
      <div className={mono ? 'mono' : ''} style={{ fontSize: 18, fontWeight: 800, marginTop: 2, letterSpacing: '-0.01em' }}>
        {value}
      </div>
    </div>
  );
}

function OrgDetailA(p: DetailProps) {
  const { lang, t } = useI18n();
  // el porcentaje del socio: su ficha en curso, sus sites, sus instructores
  // y sus documentos de compliance
  // marcar «no aplica» no toca el borrador, así que la barra no se
  // enteraría sola: este contador la obliga a recalcular
  const [, recalcular] = useReducer((n: number) => n + 1, 0);
  const c = progresoOrg(p.org.OrgId, p.draft);
  const faltan = c.total - c.hechos;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* horizontal summary strip */}
      <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {t('common.completeness')}
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span className="mono" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: c.pct === 100 ? 'var(--ok)' : 'var(--text)' }}>
              {c.pct}%
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {faltan > 0 ? `${faltan} ${lang === 'es' ? 'pendientes' : 'pending'}` : lang === 'es' ? 'sin pendientes' : 'no pending'}
            </span>
          </div>
          <div style={{ marginTop: 8 }}><ProgressBar pct={c.pct} /></div>
        </div>
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', flex: 1 }}>
          <Stat label="Sites" value={p.sites.length} />
          <Stat label={lang === 'es' ? 'Instructores' : 'Instructors'} value={p.instrCount} />
          <Stat label={lang === 'es' ? 'Año' : 'Year'} value={p.draft.YearJoined} />
          <Stat label={lang === 'es' ? 'Site primario' : 'Primary site'} value={<span style={{ fontSize: 13 }}>{p.draft.PrimarySiteId}</span>} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {p.editing ? (
            <>
              <button className="btn" onClick={() => p.setEditing(false)}>{t('common.cancel')}</button>
              <button className="btn primary" onClick={p.save}>{ic.save} {t('common.save')}</button>
            </>
          ) : !p.permiso.permanente ? (
            <button
              className="btn primary"
              onClick={() => p.setEditing(true)}
              disabled={!p.permiso.puede}
              data-tip={p.permiso.motivo || undefined}
            >
              {ic.edit} {t('common.edit')}
            </button>
          ) : (
            <span className="chip lock" data-tip={p.permiso.motivo || undefined}>{ic.lock} {t('common.readonly')}</span>
          )}
        </div>
      </div>

      <FieldLegend />

      {/* sections laid out horizontally as a responsive grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, alignItems: 'start' }}>
        {SECTIONS.map((sec) => (
          <SectionBlock
            key={sec.k}
            sectionKey={sec.k}
            fields={orgSchema}
            draft={p.draft}
            onChange={p.setField}
            record={p.draft}
            editing={p.editing}
            tone={sec.tone}
            entidad="organization"
            id={p.org.OrgId}
            onExcusar={recalcular}
            right={sec.k === 'legal_contact' ? <LegalActions permiso={p.permiso} openLegal={p.openLegal} legalPending={p.legalPending} /> : null}
          />
        ))}
      </div>

      <div className="card" style={{ padding: 12, display: 'flex', gap: 10, alignItems: 'flex-start', borderLeft: '3px solid var(--st-read)' }}>
        <span style={{ color: 'var(--st-read)' }}>{ic.info}</span>
        <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{t('legal.readonly_note')}</div>
      </div>
    </div>
  );
}

/* ---------- B: two-column workspace ---------- */

function OrgDetailB(p: DetailProps) {
  const { lang, t } = useI18n();
  // el porcentaje del socio: su ficha en curso, sus sites, sus instructores
  // y sus documentos de compliance
  // marcar «no aplica» no toca el borrador, así que la barra no se
  // enteraría sola: este contador la obliga a recalcular
  const [, recalcular] = useReducer((n: number) => n + 1, 0);
  const c = progresoOrg(p.org.OrgId, p.draft);
  const faltan = c.total - c.hechos;
  const [active, setActive] = useState('general');

  return (
    <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }}>
      <aside className="m-hide" style={{ position: 'sticky', top: 80, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
            {lang === 'es' ? 'Secciones' : 'Sections'}
          </div>
          {SECTIONS.map((s) => (
            <button
              key={s.k}
              onClick={() => {
                setActive(s.k);
                document.getElementById('sec-' + s.k)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
              }}
              className="btn ghost"
              style={{
                justifyContent: 'flex-start', padding: '7px 10px', fontWeight: active === s.k ? 700 : 500,
                background: active === s.k ? 'var(--bg-2)' : 'transparent',
                width: '100%', fontSize: 12, gap: 8,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 99, background: s.tone, flexShrink: 0 }} />
              {t('sec.' + s.k)}
            </button>
          ))}
        </div>

        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {t('common.completeness')}
          </div>
          <div style={{ marginTop: 6 }}>
            <ProgressBar pct={c.pct} label="" sub={`${faltan} ${lang === 'es' ? 'pendientes' : 'pending'}`} />
          </div>
          <div className="mono" style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: c.pct === 100 ? 'var(--ok)' : 'var(--text)' }}>
            {c.pct}%
          </div>
        </div>

        {p.editing ? (
          <button className="btn primary" onClick={p.save} style={{ justifyContent: 'center', padding: '10px 14px' }}>
            {ic.save} {t('common.save')}
          </button>
        ) : !p.permiso.permanente ? (
          <button
            className="btn primary"
            onClick={() => p.setEditing(true)}
            disabled={!p.permiso.puede}
            data-tip={p.permiso.motivo || undefined}
            style={{ justifyContent: 'center', padding: '10px 14px' }}
          >
            {ic.edit} {t('common.edit')}
          </button>
        ) : null}
      </aside>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FieldLegend />
        {SECTIONS.map((sec) => (
          <div key={sec.k} id={'sec-' + sec.k}>
            <SectionBlock
              sectionKey={sec.k}
              fields={orgSchema}
              draft={p.draft}
              onChange={p.setField}
              record={p.draft}
              editing={p.editing}
              tone={sec.tone}
              entidad="organization"
              id={p.org.OrgId}
              onExcusar={recalcular}
              right={sec.k === 'legal_contact' ? <LegalActions permiso={p.permiso} openLegal={p.openLegal} legalPending={p.legalPending} /> : null}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- C: tabbed (default) ---------- */

function OrgDetailC(p: DetailProps) {
  const { lang, t } = useI18n();
  // el porcentaje del socio: su ficha en curso, sus sites, sus instructores
  // y sus documentos de compliance
  // marcar «no aplica» no toca el borrador, así que la barra no se
  // enteraría sola: este contador la obliga a recalcular
  const [, recalcular] = useReducer((n: number) => n + 1, 0);
  const c = progresoOrg(p.org.OrgId, p.draft);
  const faltan = c.total - c.hechos;
  const [tab, setTab] = useState('general');
  const country = COUNTRIES.find((x) => x.code === p.org.OrgCountry)?.[lang === 'es' ? 'name_es' : 'name_en'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* summary header */}
      <div className="card" style={{ padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(140deg,var(--brand-primary-l),#fff)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-primary-d)',
              fontWeight: 800, fontSize: 20, border: '1px solid var(--line)',
            }}
          >
            {p.org.OrgName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
          </div>
          <div>
            <div style={{ fontSize: 19, fontWeight: 800 }}>{p.org.OrgName}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
              <span className="mono">{p.org.OrgId}</span>
              <span>·</span>
              <span>{country}</span>
              <span>·</span>
              <span>{p.org.RegisteredCity}</span>
              <span>·</span>
              <span>{lang === 'es' ? `Desde ${p.draft.YearJoined}` : `Since ${p.draft.YearJoined}`}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 200 }}>
            <ProgressBar pct={c.pct} label={t('common.completeness')} sub={`${faltan} ${lang === 'es' ? 'pendientes' : 'pending'}`} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {p.editing ? (
              <>
                <button className="btn" onClick={() => p.setEditing(false)}>{t('common.cancel')}</button>
                <button className="btn primary" onClick={p.save}>{ic.save} {t('common.save')}</button>
              </>
            ) : !p.permiso.permanente ? (
              <button
              className="btn primary"
              onClick={() => p.setEditing(true)}
              disabled={!p.permiso.puede}
              data-tip={p.permiso.motivo || undefined}
            >
              {ic.edit} {t('common.edit')}
            </button>
            ) : (
              <span className="chip lock" data-tip={p.permiso.motivo || undefined}>{ic.lock} {t('common.readonly')}</span>
            )}
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--line)', overflowX: 'auto', background: 'var(--surface-2)' }}>
          {SECTIONS.map((s) => (
            <button
              key={s.k}
              role="tab"
              aria-selected={tab === s.k}
              onClick={() => setTab(s.k)}
              className="btn tab"
              style={estiloPestana(s.tone, tab === s.k)}
            >
              <span style={puntoPestana(s.tone)} />
              {t('sec.' + s.k)}
            </button>
          ))}
        </div>
        <div style={{ padding: 18 }}>
          <FieldLegend />
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1 }}>
            {orgSchema.filter((f) => f.section === tab).map((f) => (
              <FieldRow
                key={f.key}
                field={f}
                value={p.draft[f.key]}
                onChange={p.setField}
                record={p.draft}
                editing={p.editing}
                entidad="organization"
                id={p.org.OrgId}
                onExcusar={recalcular}
              />
            ))}
          </div>
          {tab === 'legal_contact' ? (
            <div className="m-stack" style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', padding: 12, background: 'var(--st-read-l)', borderRadius: 8 }}>
              <span style={{ color: 'var(--st-read)' }}>{ic.info}</span>
              <div style={{ fontSize: 12, color: 'var(--text)', flex: 1 }}>{t('legal.readonly_note')}</div>
              <LegalActions permiso={p.permiso} openLegal={p.openLegal} legalPending={p.legalPending} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ---------- wrapper ---------- */

export function OrgDetail({ orgId }: { orgId: string }) {
  const { lang, t } = useI18n();
  const go = useGo();
  const role = useAppStore((s) => s.role);
  const user = useAppStore((s) => s.user);
  const addToast = useAppStore((s) => s.addToast);
  const variant = useAppStore((s) => s.variant.org);
  const setVariantFor = useAppStore((s) => s.setVariantFor);
  const permiso = usePuedeEditar('org');

  const org = ds.getOrg(orgId);
  const sites = ds.listSites({ orgId });
  const instrCount = ds.listAssignments({ orgId }).length;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({ ...org });
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalPending, setLegalPending] = useState(
    () => ds.listLegalRequests().some((r) => r.OrgId === orgId && r.status === 'pending'),
  );

  useEffect(() => {
    setDraft({ ...ds.getOrg(orgId) });
    setEditing(false);
    setLegalPending(ds.listLegalRequests().some((r) => r.OrgId === orgId && r.status === 'pending'));
  }, [orgId]);

  if (!org) {
    return (
      <EmptyState
        icon={ic.org}
        title={lang === 'es' ? 'Organización no encontrada' : 'Organization not found'}
        action={<button className="btn" onClick={() => go('dashboard')}>{t('common.back')}</button>}
      />
    );
  }

  function setField(k: string, v: string | string[]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  function save() {
    /* Los rojos del socio NO impiden guardar, a diferencia de los del site.
       Son los cuatro del perfil, que nadie ha contestado nunca porque no
       vienen del padrón de Autodesk: bloquear por ellos dejaría a los 134
       socios sin poder corregir ni un teléfono hasta responderlos todos.
       Se guarda, y el porcentaje sigue diciendo lo que falta.

       Un valor MAL ESCRITO sí para el guardado: un correo sin arroba, o
       «Otro» marcado sin decir cuál, no es un hueco — es un dato
       equivocado, y entra igual de mal hoy que dentro de tres meses. */
    const malos = orgSchema.filter((f) => {
      const err = validateField(f, draft[f.key], draft);
      return err !== null && err !== 'required_empty';
    });
    if (malos.length) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se puede guardar' : 'Cannot save',
        body: lang === 'es'
          ? `Revisa: ${malos.map((f) => f.label_es).join(', ')}`
          : `Check: ${malos.map((f) => f.label_en).join(', ')}`,
      });
      return;
    }

    const pendientes = orgSchema.filter(
      (f) => f.state === 'required' && validateField(f, draft[f.key], draft) === 'required_empty',
    );
    ds.updateOrg(orgId, draft as Partial<Organization>);
    setEditing(false);
    addToast({
      kind: 'save',
      celebrate: pendientes.length === 0,
      title: lang === 'es' ? 'Cambios guardados' : 'Changes saved',
      // se guarda igual, pero se dice qué sigue faltando: el socio no tiene
      // por qué adivinar por qué su porcentaje no sube
      body: pendientes.length
        ? (lang === 'es'
            ? `Quedan ${pendientes.length} campos obligatorios sin contestar.`
            : `${pendientes.length} required fields are still unanswered.`)
        : org!.OrgName,
    });
  }

  const detail: DetailProps = {
    role, org, sites, instrCount, editing, setEditing, draft, setField, save,
    openLegal: () => setLegalOpen(true),
    legalPending,
    permiso,
  };

  const crumbs =
    role === 'ETC_ADMIN'
      ? [{ label: t('nav.orgs'), onClick: () => go('org') }, { label: org.OrgName }]
      : role === 'PARTNER_MGR' && userOrgIds(user).length > 1
        ? [{ label: lang === 'es' ? 'Mis organizaciones' : 'My organizations', onClick: () => go('org') }, { label: org.OrgName }]
        : undefined;

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={org.OrgName}
        crumbs={crumbs}
        sub={`${org.OrgId} · ${COUNTRIES.find((c) => c.code === org.OrgCountry)?.[lang === 'es' ? 'name_es' : 'name_en'] ?? ''}`}
        actions={
          <VariantSwitch
            value={variant}
            onChange={(v) => setVariantFor('org', v)}
            exclude={permiso.permanente ? ['A'] : undefined}
            options={[
              { value: 'A', es: 'Clásica', en: 'Classic' },
              { value: 'B', es: 'Secciones', en: 'Sections' },
              { value: 'C', es: 'Pestañas', en: 'Tabs' },
            ]}
          />
        }
      />

      {variant === 'A' ? <OrgDetailA {...detail} /> : variant === 'B' ? <OrgDetailB {...detail} /> : <OrgDetailC {...detail} />}

      <OrgSites sites={sites} />

      <ComplianceCard orgId={org.OrgId} orgName={org.OrgName} />

      <LegalChangeModal
        open={legalOpen}
        orgName={org.OrgName}
        onClose={() => setLegalOpen(false)}
        onSend={async (d) => {
          // Si el servidor la rechaza, esto lanza y el formulario se queda
          // abierto con lo escrito: antes se cerraba pasara lo que pasara y
          // el socio perdía los datos junto con el motivo.
          await ds.addLegalRequest({
            OrgId: org.OrgId, OrgName: org.OrgName,
            first: d.first, last: d.last, email: d.email, phone: d.phone,
            doc: d.file?.name ?? '',
          }, d.file);
          setLegalPending(true);
          addToast({
            kind: 'ok',
            title: t('legal.sent'),
            body: org.OrgName,
            details: lang === 'es' ? 'Pendiente de revisión por Admin ETC' : 'Pending ETC Admin review',
            target: { screen: 'admin' },
            targetLabel: lang === 'es' ? 'Ver en Administración' : 'Open in Administration',
          });
        }}
      />
    </div>
  );
}

export default React.memo(OrgDetail);
